import React from "react";
import { Check, X, Zap, MessagesSquare, Award, ShieldCheck, DollarSign } from "lucide-react";

const rows = [
  {
    icon: Zap,
    direct: { title: "Instant Turnaround", body: "Direct workflow. No queues, no assembly lines. Your SOA goes straight to the top of the list." },
    agency: { title: "Slow Queues", body: "Multiple wait levels. Weeks of delay moving between account managers and the actual writers." },
  },
  {
    icon: MessagesSquare,
    direct: { title: "Pure Direct Messaging", body: "I sit directly on your Teams or Slack. Zero friction, zero relay, zero miscommunication." },
    agency: { title: "Relay Communication", body: "Tickets and account managers. Complex strategies get lost in translation." },
  },
  {
    icon: Award,
    direct: { title: "Senior Mastery", body: "Advanced modeling for complex strategies. SMSFs, Family Trusts, gearing. All handled personally." },
    agency: { title: "Entry Level Staff", body: "Work often pushed to junior staff. Expertise variable, unverifiable, inconsistent." },
  },
  {
    icon: ShieldCheck,
    direct: { title: "Flawless Compliance", body: "Audit ready documents meeting strict BID and RG90 standards. Every single file." },
    agency: { title: "Compliance Risk", body: "Junior staff often lack deep Australian legislative grounding. Your licence is on the line." },
  },
  {
    icon: DollarSign,
    direct: { title: "Transparent Flat Fees", body: "Pay only for the SOA, from $150 to $500 AUD with a hard cap. No retainers, no setup costs, no surprises." },
    agency: { title: "Hidden Fees", body: "Monthly retainers, management fees, surprise overages. Costs balloon over time." },
  },
];

const DirectAdvantage = () => {
  return (
    <section
      id="advantage"
      data-testid="advantage-section"
      className="relative bg-[#FACC15] border-b-2 border-black"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-20 md:py-28">
        <div className="max-w-2xl">
          <span className="inline-block bg-black text-white border-2 border-black px-3 py-1 text-xs font-bold uppercase tracking-widest">
            The Direct Advantage
          </span>
          <h2 className="font-display font-black mt-4 text-4xl md:text-5xl tracking-tighter leading-[1.05]">
            Why work direct, not through an agency.
          </h2>
          <p className="mt-5 text-base md:text-lg text-black/85 leading-relaxed">
            The difference between a specialist and a BPO assembly line is not just price. It is
            <em> everything</em>. Speed. Quality. Compliance. Communication. Cost.
          </p>
        </div>

        <div className="mt-12 grid lg:grid-cols-2 gap-6 lg:gap-8">
          {/* Banana column */}
          <div className="bg-white border-2 border-black shadow-brutal-lg p-6 md:p-8">
            <div className="flex items-center gap-3 pb-4 border-b-2 border-black">
              <span className="inline-flex items-center justify-center w-10 h-10 bg-[#FACC15] border-2 border-black">
                <Check className="w-5 h-5 text-black" strokeWidth={3} />
              </span>
              <div>
                <p className="font-display font-black text-xl leading-none">Banana Paraplanner</p>
                <p className="text-xs font-bold uppercase tracking-widest mt-1 text-black/60">Frictionless · Specialized · Compliant</p>
              </div>
            </div>
            <ul className="mt-5 space-y-4">
              {rows.map((r, i) => {
                const Icon = r.icon;
                return (
                  <li key={`d-${i}`} className="flex gap-3" data-testid={`advantage-direct-${i}`}>
                    <span className="shrink-0 w-9 h-9 border-2 border-black bg-[#FACC15] flex items-center justify-center">
                      <Icon className="w-4 h-4" />
                    </span>
                    <div>
                      <p className="font-display font-bold text-base leading-tight">{r.direct.title}</p>
                      <p className="text-sm text-black/75 mt-1 leading-relaxed">{r.direct.body}</p>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Agency column */}
          <div className="bg-black text-white border-2 border-black shadow-brutal p-6 md:p-8">
            <div className="flex items-center gap-3 pb-4 border-b-2 border-white/30">
              <span className="inline-flex items-center justify-center w-10 h-10 bg-white border-2 border-white">
                <X className="w-5 h-5 text-black" strokeWidth={3} />
              </span>
              <div>
                <p className="font-display font-black text-xl leading-none">Typical BPO Agency</p>
                <p className="text-xs font-bold uppercase tracking-widest mt-1 text-white/60">Slow · Opaque · Inconsistent</p>
              </div>
            </div>
            <ul className="mt-5 space-y-4">
              {rows.map((r, i) => {
                const Icon = r.icon;
                return (
                  <li key={`a-${i}`} className="flex gap-3" data-testid={`advantage-agency-${i}`}>
                    <span className="shrink-0 w-9 h-9 border-2 border-white/40 bg-white/5 flex items-center justify-center">
                      <Icon className="w-4 h-4 text-white/80" />
                    </span>
                    <div>
                      <p className="font-display font-bold text-base leading-tight text-white">{r.agency.title}</p>
                      <p className="text-sm text-white/70 mt-1 leading-relaxed">{r.agency.body}</p>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>

        <p className="mt-10 max-w-3xl text-sm md:text-base text-black/80 italic">
          The direct model is not just more efficient. It is the smarter commercial decision for your practice.
        </p>
      </div>
    </section>
  );
};

export default DirectAdvantage;
